/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import React from 'react';

import classnames from 'classnames';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

let selectHtmlElement: React.RefObject<HTMLSelectElement>;

/**
 * Class names used in component.
 */
const componentClassName = 'msc-refiner-sort';
const sortOptionsClassName = `${componentClassName}__sort-options`;
const sortOptionClassName = `${componentClassName}__sort-option`;
const sortOptionSelectedClassName = `${componentClassName}__sort-option-selected`;
const labelId = `${componentClassName}__label`;
const selectLabelId = `${componentClassName}__select`;
const labelClassName = `${componentClassName}__label`;

/**
 * IRefinerSortComponent interface.
 */
interface IRefinerSortComponent extends IComponent<IRefinerSortProps> {}

/**
 * IRefinerSortData interface.
 */
export interface IRefinerSortData {
    options: IRefinerSortOption[];
}

/**
 * IRefinerSortOption interface.
 */
export interface IRefinerSortOption {
    label: string;
    value: string;
}

/**
 * IRefinerSortProps interface.
 */
export interface IRefinerSortProps extends IComponentProps<IRefinerSortData> {
    className?: string;
    currentSortOption?: IRefinerSortOption;
    label?: string;
    ariaLabel?: string;
    isDisabled?: boolean;
    telemetryContent?: ITelemetryContent;
    onSelectOption?(newSortOption?: IRefinerSortOption): void;
}

/**
 * RefinerSort component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerSortFunctionalComponent: React.FC<IRefinerSortProps> = (props: IRefinerSortProps) => {
    const [sortOptionState, setSortOptionState] = React.useState(props.currentSortOption);

    const onSelectOptionHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const sortOption = props.data.options?.find((option: IRefinerSortOption) => option.value === value);
        setSortOptionState(sortOption);
        props.onSelectOption?.(sortOption);
    };

    // TODO: define telemetry attributes
    // const attributes = getTelemetryAttributes(
    //     props.telemetryContent!,
    //     getPayloadObject('click', props.telemetryContent!, TelemetryConstant.Sort)
    // );

    return (
        <div className={classnames(props.className, componentClassName)}>
            {ArrayExtensions.hasElements(props.data.options) && (
                <label id={labelId} htmlFor={selectLabelId} className={labelClassName}>
                    {props.label}
                    <select
                        id={selectLabelId}
                        ref={selectHtmlElement}
                        className={sortOptionsClassName}
                        value={sortOptionState?.value}
                        title={props.ariaLabel ?? ''}
                        disabled={props.isDisabled}
                        aria-label={props.ariaLabel ?? ''}
                        aria-disabled={props.isDisabled}
                        onChange={onSelectOptionHandler}
                    >
                        {props.data.options?.map((option: IRefinerSortOption) => {
                            return (
                                <option
                                    key={option.value}
                                    value={option.value}
                                    aria-label={option.label}
                                    aria-selected={option.value === sortOptionState?.value}
                                    className={classnames(
                                        sortOptionClassName,
                                        option.value === sortOptionState?.value ? sortOptionSelectedClassName : ''
                                    )}
                                >
                                    {option.label}
                                </option>
                            );
                        })}
                    </select>
                </label>
            )}
        </div>
    );
};

/**
 * RefinerSort component.
 */
export const RefinerSortComponent: React.FunctionComponent<IRefinerSortProps> = msdyn365Commerce.createComponent<IRefinerSortComponent>(
    'Refiner-Sort',
    { component: React.memo(RefinerSortFunctionalComponent) }
);
